import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import {
  LINKED_BADGE_COLORS,
  PERSON_AVATAR_INITIALS_COLOR,
  getPersonAvatarColor,
  getPersonInitials,
} from '../../utils/personAvatar';

interface SaleCustomerBarProps {
  name: string;
  /** Customer exists in the back office: shows the cloud badge the Save Order sheet uses. */
  linked?: boolean;
  /** When given, shows × to take the customer off the sale. */
  onRemove?: () => void;
  /** Rounded, bordered strip for a padded column instead of an edge-to-edge bar. */
  inset?: boolean;
}

/** One-line blue bar under the header on Products and Basket naming the customer the sale is for. */
const SaleCustomerBar: React.FC<SaleCustomerBarProps> = ({
  name,
  linked = false,
  onRemove,
  inset = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <View
      style={[styles.bar, inset && styles.barInset]}
      accessibilityLabel={`Customer on this sale: ${name}`}>
      <View
        style={[
          styles.avatar,
          { backgroundColor: getPersonAvatarColor(name) },
        ]}>
        <Text style={styles.avatarText}>
          {getPersonInitials(name)}
        </Text>
        {linked ? (
          <View style={styles.linkedBadge}>
            <FontAwesome6
              name="cloud"
              iconStyle="solid"
              size={fs(7)}
              color={LINKED_BADGE_COLORS.icon}
            />
          </View>
        ) : null}
      </View>
      <View style={styles.textBlock}>
        <Text style={styles.label}>Customer</Text>
        <Text style={styles.name}>{name}</Text>
      </View>
      {onRemove ? (
        <Pressable
          onPress={onRemove}
          accessibilityRole="button"
          accessibilityLabel={`Remove ${name} from this sale`}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          style={({ pressed, hovered }: any) => [
            styles.remove,
            (pressed || (Platform.OS === 'web' && hovered)) &&
              styles.removePressed,
          ]}>
          <FontAwesome6
            name="xmark"
            iconStyle="solid"
            size={iconSize(18)}
            color={theme.colors.textSecondary}
          />
        </Pressable>
      ) : null}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      minHeight: v(36),
      paddingLeft: s(16),
      paddingRight: s(4),
      backgroundColor: theme.colors.primarySurface,
      borderBottomWidth: borderWidth(1),
      borderBottomColor: theme.colors.infoLight,
    },
    barInset: {
      marginTop: v(12),
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.infoLight,
    },
    avatar: {
      width: s(28),
      height: s(28),
      borderRadius: s(14),
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      lineHeight: fs(14),
      color: PERSON_AVATAR_INITIALS_COLOR,
    },
    linkedBadge: {
      position: 'absolute',
      right: -2,
      bottom: -2,
      width: s(14),
      height: s(14),
      borderRadius: s(7),
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.primarySurface,
      backgroundColor: LINKED_BADGE_COLORS.background,
      alignItems: 'center',
      justifyContent: 'center',
    },
    textBlock: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'baseline',
      columnGap: s(8),
    },
    label: {
      flexShrink: 0,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(15),
      color: theme.colors.primaryDark,
    },
    name: {
      flex: 1,
      minWidth: 0,
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    remove: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    removePressed: {
      backgroundColor: 'rgba(21, 96, 189, 0.08)',
    },
  });

export default SaleCustomerBar;
